import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atomWithSuspenseQuery } from 'jotai-tanstack-query';
import { atomWithDebounce } from '@/atoms/debounce';
import { teamMetricsAtom } from '@/atoms/metric/teamMetrics';
import { teamIdAtom } from '@/atoms/team/team';
import { teamNormalisedAggregatesUrl } from '@/query/url';
import { Nullable } from '@/types/generic';
import { NestedObject } from '@/types/object';
import { hasEmptyInFilters } from '@/utils/api';
import { loadable } from '@/utils/jotai';
import { fetchClientAtom } from '../client';
import { radarDataAtom } from '../radar';
import { teamRadarTableColumnsAtom } from '../tableColumns';
import { teamRadarTemplatesAtom } from '../userConfigs';

export const teamRadarFilterDebounceObject = atomWithDebounce<Nullable<NestedObject>>(null);
const { debouncedValueAtom: teamRadarFilterAtom } = teamRadarFilterDebounceObject;

const rawTeamRadarDataAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['teamRadarData', get(teamIdAtom), get(teamRadarFilterAtom)] as const;

  const queryFn = async ({ queryKey: [, teamId, filterParams] }: { queryKey: typeof queryKey }) => {
    const url = teamNormalisedAggregatesUrl(teamId, filterParams);
    if (!url || hasEmptyInFilters(filterParams)) return {};

    return (await fetch(url)) as Promise<Record<string, number>>;
  };

  return { queryKey, queryFn };
});

export { teamMetricsAtom } from '@/atoms/metric/teamMetrics';

export const loadableTeamRadarDataAtom = loadable(
  radarDataAtom(rawTeamRadarDataAtom, teamMetricsAtom, teamRadarTemplatesAtom, teamRadarTableColumnsAtom),
);

export const teamAggsMinutesPlayedAtom = atom(async get => {
  const rawTeamRadarData = (await get(rawTeamRadarDataAtom)).data;
  return rawTeamRadarData.minutes_played || 0;
});

export const unwrappedTeamAggsMinutesPlayedAtom = unwrap(teamAggsMinutesPlayedAtom, prev => prev || 0);

const teamAggsMinutesPlayed360Atom = atom(async get => {
  const rawTeamRadarData = (await get(rawTeamRadarDataAtom)).data;
  return rawTeamRadarData.minutes_played_360 || 0;
});

export const unwrappedTeamAggsMinutesPlayed360Atom = unwrap(teamAggsMinutesPlayed360Atom, prev => prev || 0);
